import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { ApiService } from '../../api.service';
import { ServicoTenant } from '../tenant/servico-tenant';
import { ServicoDecisaoAutorizacaoTerminal } from './servico-decisao-autorizacao-terminal';
import { ServicoEstadoTerminalLocal } from './servico-estado-terminal-local';

export interface EstadoTerminalPublico {
  autorizado: boolean;
  dispositivoId?: string;
  nome: string;
  tokenTerminal: string;
  dataHoraServidor?: string;
  autorizacaoOfflineExpiraEm?: string;
  zoomCamera?: number;
  areaLeituraCameraPercentual?: number;
}

@Injectable({ providedIn: 'root' })
export class ServicoTerminalPublico {
  constructor(
    private readonly api: ApiService,
    private readonly servicoTenant: ServicoTenant,
    private readonly servicoEstadoTerminalLocal: ServicoEstadoTerminalLocal,
    private readonly servicoDecisaoAutorizacaoTerminal: ServicoDecisaoAutorizacaoTerminal
  ) {}

  get tokenSalvo() {
    return localStorage.getItem(this.servicoTenant.chaveTokenTerminal) || '';
  }

  estadoVazio(): EstadoTerminalPublico {
    return {
      autorizado: false,
      dispositivoId: '',
      nome: '',
      tokenTerminal: ''
    };
  }

  estadoPersistido(): EstadoTerminalPublico | null {
    const tokenTerminal = this.tokenSalvo;
    if (!tokenTerminal) {
      return null;
    }

    const local = this.servicoEstadoTerminalLocal.estadoAtual();
    if (local && local.tokenTerminal === tokenTerminal) {
      return {
        autorizado: true,
        dispositivoId: local.dispositivoId,
        nome: local.nome,
        tokenTerminal,
        dataHoraServidor: local.dataHoraServidor,
        autorizacaoOfflineExpiraEm: local.autorizacaoOfflineExpiraEm,
        zoomCamera: local.zoomCamera,
        areaLeituraCameraPercentual: local.areaLeituraCameraPercentual
      };
    }

    return {
      autorizado: true,
      nome: '',
      tokenTerminal
    };
  }

  estadoAutorizado(terminal: EstadoTerminalPublico): EstadoTerminalPublico {
    return {
      autorizado: terminal.autorizado,
      dispositivoId: terminal.dispositivoId,
      nome: terminal.nome,
      tokenTerminal: terminal.tokenTerminal,
      dataHoraServidor: terminal.dataHoraServidor,
      autorizacaoOfflineExpiraEm: terminal.autorizacaoOfflineExpiraEm,
      zoomCamera: terminal.zoomCamera,
      areaLeituraCameraPercentual: terminal.areaLeituraCameraPercentual
    };
  }

  ehRecusaDefinitiva(erro: unknown) {
    return this.servicoDecisaoAutorizacaoTerminal.ehRecusaDefinitiva(erro);
  }

  mensagemFalhaValidacao(erro: unknown) {
    return this.servicoDecisaoAutorizacaoTerminal.mensagemFalhaValidacao(erro);
  }

  validarTokenSalvo(): Observable<EstadoTerminalPublico> {
    const token = this.tokenSalvo;
    if (!token) {
      return throwError(() => new Error('Terminal sem token salvo'));
    }

    return this.api.validarTerminalPublico(token).pipe(
      map(terminal => {
        const tokenTerminal = terminal.tokenTerminal || token;
        this.servicoEstadoTerminalLocal.registrarValidacao(terminal, tokenTerminal);
        return this.estadoDoTerminal(terminal, tokenTerminal);
      }),
      catchError(erro => {
        this.servicoDecisaoAutorizacaoTerminal.aoFalharValidacao(erro, () => this.limparToken());
        return throwError(() => erro);
      })
    );
  }

  ativar(codigoServidor: string): Observable<EstadoTerminalPublico> {
    return this.api.ativarTerminalPublico(codigoServidor).pipe(
      tap(terminal => localStorage.setItem(this.servicoTenant.chaveTokenTerminal, terminal.tokenTerminal)),
      map(terminal => {
        this.servicoEstadoTerminalLocal.registrarValidacao(terminal, terminal.tokenTerminal);
        return this.estadoDoTerminal(terminal, terminal.tokenTerminal);
      })
    );
  }

  limparToken() {
    localStorage.removeItem(this.servicoTenant.chaveTokenTerminal);
    this.servicoEstadoTerminalLocal.limpar();
  }

  private estadoDoTerminal(terminal: EstadoTerminalPublico, tokenTerminal: string): EstadoTerminalPublico {
    return {
      autorizado: terminal.autorizado,
      dispositivoId: terminal.dispositivoId,
      nome: terminal.nome,
      tokenTerminal,
      dataHoraServidor: terminal.dataHoraServidor,
      autorizacaoOfflineExpiraEm: terminal.autorizacaoOfflineExpiraEm,
      zoomCamera: terminal.zoomCamera,
      areaLeituraCameraPercentual: terminal.areaLeituraCameraPercentual
    };
  }
}
